import { useState, useEffect } from 'react';
import { Search, ChevronLeft, ChevronRight, Clock, CheckCircle, XCircle, Loader, AlertTriangle } from 'lucide-react';
import { adminApi } from '../utils/api';
import Layout from '../components/Layout';
import Header from '../components/Header';
import { Button, Card, Input, Select, Badge, Spinner, ErrorState } from '../components/ui';
import { ResponsiveTable, type Column } from '../components/ResponsiveTable';
import { statusToBadgeVariant, statusToLabel, classifyFailure } from '../utils/statusBadge';
import type { User, TranscriptionStatus } from '../types';

interface AdminTranscriptionsPageProps {
  user: User;
}

/**
 * 表示用のステータス。DB の status に、プラン時間超過を表す派生値 quota_exceeded を加えたもの。
 * DB 側は CHECK 制約で失敗が 'failed' に集約されるため、種別は error_message から判別する
 * （サーバが failure_kind として確定した値を返す）。
 */
type DisplayStatus = TranscriptionStatus | 'quota_exceeded';

interface AdminTranscription {
  id: string;
  user_id: string;
  status: TranscriptionStatus;
  error_message: string | null;
  failure_kind: DisplayStatus | null;
  audio_filename: string;
  audio_duration: number | null;
  processing_time: number | null;
  created_at: string;
  completed_at: string | null;
}

const STATUS_ICONS: Record<DisplayStatus, React.ReactNode> = {
  pending: <Clock className="w-4 h-4" />,
  processing: <Loader className="w-4 h-4 animate-spin" />,
  completed: <CheckCircle className="w-4 h-4" />,
  partial: <CheckCircle className="w-4 h-4" />,
  failed: <XCircle className="w-4 h-4" />,
  cancelled: <XCircle className="w-4 h-4" />,
  quota_exceeded: <AlertTriangle className="w-4 h-4" />,
};

/** 行の表示用ステータス。サーバの failure_kind を優先し、無ければ error_message から導く。 */
function displayStatusOf(t: AdminTranscription): DisplayStatus {
  return t.failure_kind ?? (classifyFailure(t.status, t.error_message) as DisplayStatus);
}

function StatusBadge({ status }: { status: DisplayStatus }) {
  return (
    <Badge variant={statusToBadgeVariant(status)} className="gap-1">
      {STATUS_ICONS[status]}
      {statusToLabel(status)}
    </Badge>
  );
}

export default function AdminTranscriptionsPage({ user }: AdminTranscriptionsPageProps) {
  const [transcriptions, setTranscriptions] = useState<AdminTranscription[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [filterStatus, setFilterStatus] = useState<string>('');
  const [filterUserId, setFilterUserId] = useState('');

  // 意図的: page / filterStatus の変化で即時再取得する。
  // filterUserId（テキスト）は依存に含めず、検索ボタン送信（handleSearch）でのみ反映する（元実装の挙動踏襲）。
  useEffect(() => {
    loadTranscriptions();
  }, [page, filterStatus]);

  const loadTranscriptions = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await adminApi.getTranscriptions(
        page,
        50,
        filterStatus || undefined,
        filterUserId || undefined
      );
      setTranscriptions(data.transcriptions);
    } catch (err) {
      console.error('Failed to load transcriptions:', err);
      setError('書き起こし一覧の読み込みに失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    loadTranscriptions();
  };

  const formatDuration = (seconds: number | null) => {
    if (seconds === null) return '-';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatDateTime = (dateStr: string | null) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleString('ja-JP', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getProcessingRatio = (audioSec: number | null, processSec: number | null) => {
    if (!audioSec || !processSec || processSec === 0) return '-';
    return `${(audioSec / processSec).toFixed(1)}x`;
  };

  const columns: Column<AdminTranscription>[] = [
    {
      key: 'id',
      header: 'ID',
      render: (t) => (
        <div>
          <div className="text-xs font-mono text-text-sub">{t.id.slice(0, 8)}...</div>
          <div className="text-xs text-text-sub">User: {t.user_id.slice(0, 8)}...</div>
        </div>
      ),
    },
    {
      key: 'filename',
      header: 'ファイル名',
      render: (t) => (
        <div className="max-w-[200px] truncate" title={t.audio_filename}>
          {t.audio_filename}
        </div>
      ),
    },
    {
      key: 'status',
      header: 'ステータス',
      render: (t) => (
        <div className="space-y-1">
          <StatusBadge status={displayStatusOf(t)} />
          {t.error_message && (
            <div className="max-w-[240px] text-xs text-text-sub break-words" title={t.error_message}>
              {t.error_message}
            </div>
          )}
        </div>
      ),
    },
    { key: 'audio', header: '音声時間', align: 'right', render: (t) => <span className="font-mono">{formatDuration(t.audio_duration)}</span> },
    { key: 'proc', header: '処理時間', align: 'right', render: (t) => <span className="font-mono">{formatDuration(t.processing_time)}</span> },
    { key: 'ratio', header: '速度比', align: 'right', render: (t) => <span className="font-mono">{getProcessingRatio(t.audio_duration, t.processing_time)}</span> },
    { key: 'created', header: '作成日時', render: (t) => <span className="text-sm">{formatDateTime(t.created_at)}</span> },
    { key: 'completed', header: '完了日時', render: (t) => <span className="text-sm">{formatDateTime(t.completed_at)}</span> },
  ];

  return (
    <Layout>
      <Header user={user} title="otomochi - 書き起こし管理" />

      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* フィルター */}
        <Card className="mb-6">
          <form onSubmit={handleSearch} className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 z-10" />
                <Input
                  type="text"
                  value={filterUserId}
                  onChange={(e) => setFilterUserId(e.target.value)}
                  placeholder="ユーザーIDでフィルター..."
                  aria-label="ユーザーIDでフィルター"
                  className="pl-10"
                />
              </div>
            </div>
            <div>
              <Select
                value={filterStatus}
                aria-label="ステータスで絞り込み"
                onChange={(e) => {
                  setFilterStatus(e.target.value);
                  setPage(1);
                }}
              >
                <option value="">全ステータス</option>
                <option value="pending">待機中</option>
                <option value="processing">処理中</option>
                <option value="completed">完了</option>
                <option value="partial">部分完了</option>
                <option value="failed">失敗</option>
                <option value="quota_exceeded">超過エラー</option>
                <option value="cancelled">キャンセル</option>
              </Select>
            </div>
            <Button type="submit">検索</Button>
          </form>
        </Card>

        {/* 統計サマリー（7ステータスを均等配置。件数は表示中のページ内の集計） */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4 mb-6">
          {(['pending', 'processing', 'completed', 'partial', 'failed', 'quota_exceeded', 'cancelled'] as DisplayStatus[]).map((status) => {
            // 超過エラーは failed から切り出した派生値のため、表示用ステータスで数える
            // （failed と quota_exceeded が二重に数えられないようにする）
            const count = transcriptions.filter(t => displayStatusOf(t) === status).length;
            const selected = filterStatus === status;
            return (
              <Card
                key={status}
                interactive
                padding="sm"
                role="button"
                tabIndex={0}
                aria-pressed={selected}
                className={`text-center cursor-pointer ${selected ? 'ring-2 ring-primary' : ''}`}
                onClick={() => {
                  setFilterStatus(selected ? '' : status);
                  setPage(1);
                }}
              >
                <div className="inline-flex">
                  <StatusBadge status={status} />
                </div>
                <div className="text-2xl font-bold mt-2">{count}</div>
              </Card>
            );
          })}
        </div>

        {/* 書き起こし一覧 */}
        {loading ? (
          <div className="text-center py-12">
            <Spinner size="lg" className="mx-auto" />
            <p className="mt-4 text-text-sub">読み込み中...</p>
          </div>
        ) : error ? (
          <ErrorState message={error} onRetry={loadTranscriptions} />
        ) : (
          <>
            <Card padding="none" className="overflow-hidden p-4">
              <ResponsiveTable
                columns={columns}
                rows={transcriptions}
                rowKey={(t) => t.id}
                emptyMessage="書き起こしが見つかりません"
                caption="書き起こし一覧"
              />
            </Card>

            {/* ページネーション */}
            <div className="flex justify-center items-center gap-4 mt-6">
              <Button
                variant="secondary"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="gap-1"
              >
                <ChevronLeft className="w-4 h-4" />
                前へ
              </Button>
              <span className="text-text-sub">ページ {page}</span>
              <Button
                variant="secondary"
                onClick={() => setPage(p => p + 1)}
                disabled={transcriptions.length < 50}
                className="gap-1"
              >
                次へ
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </>
        )}
      </main>
    </Layout>
  );
}
